/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { call, select } from 'redux-saga/effects';
import {
  ActionsEventActionNames,
  Asset,
  AssetUtils,
  Ducks,
  FileFormat,
  ProductPrice,
  Stream,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { Selectors as NavigationPathSelectors, NavigationPathSections } from './navigationPathWatcher';
import { sectionsToNavigationPath } from './navigationUtils';

function getActionName(asset: Asset, stream: Stream, isLive?: boolean): ActionsEventActionNames | undefined {
  if (AssetUtils.isEpg(asset)) {
    if (isLive) {
      return ActionsEventActionNames.LIVE;
    }
    return ActionsEventActionNames.ROLLING_BUFFER;
  }
  if (AssetUtils.isRecording(asset)) {
    return ActionsEventActionNames.NPVR;
  }
  if (AssetUtils.isVod(asset)) {
    if (stream.fileFormat === FileFormat.TRAILER) {
      return ActionsEventActionNames.TRAILER;
    }
    if (asset.contentTags.isVodCatchup) {
      return ActionsEventActionNames.VOD_CATCHUP;
    }
    return ActionsEventActionNames.RENTAL; // TODO: Distinguish between Rented, Purchased, Free and Subscription VODs
  }
  return undefined;
}

// PUBLIC
export function* addWatchActionEvent(asset: Asset, stream: Stream, isLive?: boolean, error?: XError): SagaIterator {
  const sections: NavigationPathSections[] = yield select(NavigationPathSelectors.getSections);
  const actionName = getActionName(asset, stream, isLive);
  const path = sectionsToNavigationPath(sections, actionName);
  if (actionName) {
    yield call(Ducks.ActionsEvents.addWatchActionEvent, actionName, path, asset, error);
  }
}

export function* addStartOverActionEvent(asset: Asset, stream: Stream, error?: XError): SagaIterator {
  const sections: NavigationPathSections[] = yield select(NavigationPathSelectors.getSections);
  const actionName = getActionName(asset, stream);
  const path = sectionsToNavigationPath(sections, actionName);
  if (actionName) {
    yield call(Ducks.ActionsEvents.addStartOverActionEvent, actionName, path, asset, error);
  }
}

export function* addRecordingActionEvent(
  actionName: ActionsEventActionNames,
  asset?: Asset,
  error?: XError
): SagaIterator {
  const sections: NavigationPathSections[] = yield select(NavigationPathSelectors.getSections);
  const path = sectionsToNavigationPath(sections, actionName);
  yield call(Ducks.ActionsEvents.addRecordingActionEvent, actionName, path, asset, error);
}

export function* addWishlistActionEvent(
  actionName: ActionsEventActionNames,
  asset?: Asset,
  error?: XError
): SagaIterator {
  const sections: NavigationPathSections[] = yield select(NavigationPathSelectors.getSections);
  const path = sectionsToNavigationPath(sections, actionName);
  yield call(Ducks.ActionsEvents.addWishlistActionEvent, actionName, path, asset, error);
}

export function* addSettingsActionEvent(
  actionName: ActionsEventActionNames,
  newValue?: string,
  previousValue?: string,
  error?: XError
): SagaIterator {
  const sections: NavigationPathSections[] = yield select(NavigationPathSelectors.getSections);
  const path = sectionsToNavigationPath(sections, actionName);
  yield call(Ducks.ActionsEvents.addSettingsActionEvent, actionName, path, newValue, previousValue, error);
}

export function* addPinToggleActionEvent(value: boolean, error?: XError): SagaIterator {
  const actionName = value ? ActionsEventActionNames.PIN_ON : ActionsEventActionNames.PIN_OFF;
  const sections: NavigationPathSections[] = yield select(NavigationPathSelectors.getSections);
  const path = sectionsToNavigationPath(sections, actionName);
  const newValue = `${value}`;
  const previousValue = `${!value}`;
  yield call(Ducks.ActionsEvents.addSettingsActionEvent, actionName, path, newValue, previousValue, error);
}

export function* addRentActionEvent(
  actionName: ActionsEventActionNames,
  asset?: Asset,
  price?: ProductPrice,
  buy?: boolean,
  error?: XError
): SagaIterator {
  const sections: NavigationPathSections[] = yield select(NavigationPathSelectors.getSections);
  const path = sectionsToNavigationPath(sections, actionName);
  yield call(Ducks.ActionsEvents.addRentActionEvent, actionName, path, asset, price, buy, error);
}
